import { useQuery } from '@apollo/client';
import { GET_SELF } from 'graphql/queries';
import { useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 500;

// Access is gated on an ACTIVE subscription (endDate in the future) — matching the
// backend's entitlement enforcement. We distinguish the two "no access" cases so the
// UI can show the right thing:
//   - 'none'    : never subscribed        -> registration + checkout modal
//   - 'expired' : subscribed but lapsed   -> renewal modal (don't re-collect their profile)
//   - 'active'  : has a live subscription -> full access, no modal
export type SubscriptionStatus = 'loading' | 'active' | 'expired' | 'none';

export function useCheckSubscription() {
    const [status, setStatus] = useState<SubscriptionStatus>('loading');
    const { data, startPolling, stopPolling, error } = useQuery(GET_SELF, {
        fetchPolicy: 'network-only'
    });

    useEffect(() => {
        startPolling(POLL_INTERVAL_MS);

        if (data?.me) {
            stopPolling();

            const subs = data.me.productSubscriptions ?? [];
            const now = Date.now();
            const hasActive = subs.some((s) => new Date(s.endDate).getTime() > now);

            if (hasActive) {
                setStatus('active');
            } else if (subs.length > 0) {
                setStatus('expired');
            } else {
                setStatus('none');
            }
        }

        return () => {
            stopPolling();
        };
    }, [data?.me, startPolling, stopPolling]);

    useEffect(() => {
        if (error) {
            startPolling(POLL_INTERVAL_MS);
        }
    }, [error, startPolling]);

    const loading = !data?.me;

    return { status, loading };
}
